import agent from 'agent'
import { getAWSIntegrationSpecificValues } from 'components/status-checks/authentication-form-utils'
import { DEFAULT_POLL_INTERVAL } from 'components/status-checks/common/PollingIntervalField'
import {
  type EvaluationConfiguration,
  type EvaluationResults
} from 'types/api/status-checks/evaluation'
import {
  type EndpointConfiguration,
  type EndpointResponse,
  ObservabilityToolType,
  PredicateType,
  type ResponseBodyEvaluation,
  type ServiceStatusCheckFromStatusCheck,
  type StatusCheck,
  StatusCheckEvaluationPredicate,
  type StatusCheckHeader,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import {
  convertToK6Url,
  k6LoadGeneratorIntegration
} from 'utils/load-generators/load-generator-integrations-util'
import { asyncRetryWithBackoff } from 'utils/retry'
import {
  appdynamicsResponseBodyEvaluationConfiguration,
  appdynamicsStatusCheckIntegration,
  convertToAppDynamicsBaseUrl,
  convertToAppDynamicsUrl
} from 'utils/status-checks/status-check-integrations-util-appdynamics'
import {
  awsResponseBodyEvaluationConfiguration,
  awsStatusCheckIntegration,
  convertToAWSApiUrl
} from 'utils/status-checks/status-check-integrations-util-aws'
import {
  convertToDatadogApiUrl,
  datadogResponseBodyEvaluationConfiguration,
  dataDogStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog'
import {
  convertToDatadogEUApiUrl,
  datadogEUResponseBodyEvaluationConfiguration,
  dataDogEUStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-eu'
import {
  convertToDatadogUS1FedApiUrl,
  datadogUS1FedResponseBodyEvaluationConfiguration,
  dataDogUS1FedStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-us1-fed'
import {
  convertToDatadogUS3ApiUrl,
  datadogUS3ResponseBodyEvaluationConfiguration,
  dataDogUS3StatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-us3'
import {
  convertToDatadogUS5ApiUrl,
  datadogUS5ResponseBodyEvaluationConfiguration,
  dataDogUS5StatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-us5'
import {
  convertToDynatraceBaseUrl,
  convertToDynatraceUrl,
  dynatraceResponseBodyEvaluationConfiguration,
  dynatraceStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-dynatrace'
import {
  convertToGrafanaBaseUrl,
  convertToGrafanaUrl,
  formatResponseBodyEvaluationPredicatesGrafana,
  grafanaResponseBodyEvaluationConfiguration,
  grafanaStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-grafana'
import {
  computeNewRelicResponseBodyEvaluationConfiguration,
  convertToNewRelicUrl,
  newRelicStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-newrelic'
import {
  convertToPagerDutyUrl,
  pagerDutyResponseBodyEvaluationConfiguration,
  pagerDutyStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-pagerduty'
import { prometheusStatusCheckIntegration } from 'utils/status-checks/status-check-integrations-util-prometheus'
import { parseUrl } from 'utils/strings'
import {
  ExternalIntegrationType,
  IntegrationType
} from 'utils/team-external-integrations/team-external-integration-types'
import { ENDPOINT_URLS } from 'utils/third-party'

import type { StatusCheckFormEvaluationConfiguration } from 'components/status-checks/status-checks-form-utils'

export const customStatusCheckIntegration: TeamExternalIntegration = {
  name: 'Custom',
  type: IntegrationType.Custom,
  authUrl: '',
  isPrivateNetworkIntegration: false,
  headers: [],
  url: '',
  sortIndex: 8
}

export const customLoadGeneratorIntegration: TeamExternalIntegration = {
  name: 'Other',
  type: IntegrationType.LoadGeneratorCustom,
  authUrl: '',
  isPrivateNetworkIntegration: false,
  headers: [],
  url: '',
  sortIndex: 9,
  isConfigured: false,
  isOther: true
}

export const teamExternalIntegrationsObject = {
  pagerduty: pagerDutyStatusCheckIntegration,
  newrelic: newRelicStatusCheckIntegration,
  datadog: dataDogStatusCheckIntegration,
  datadog_us3: dataDogUS3StatusCheckIntegration,
  datadog_us5: dataDogUS5StatusCheckIntegration,
  datadog_eu: dataDogEUStatusCheckIntegration,
  datadog_us1_fed: dataDogUS1FedStatusCheckIntegration,
  grafana: grafanaStatusCheckIntegration,
  dynatrace: dynatraceStatusCheckIntegration,
  appdynamics: appdynamicsStatusCheckIntegration,
  k6: k6LoadGeneratorIntegration,
  aws: awsStatusCheckIntegration
}

export const defaultStatusCheckEvaluationConfiguration: StatusCheckFormEvaluationConfiguration =
  {
    healthyStatusCode: '200',
    isCustomRequestTimeout: false,
    requestTimeout: null,
    responseBodyEvaluation: {
      predicates: [],
      op: 'AND'
    }
  }

export const defaultPredicatesValue: StatusCheckEvaluationPredicate[] = [
  {
    type: PredicateType.String,
    jpQuery: '',
    jqQuery: '',
    comparator: '',
    rValue: ''
  }
]

export function getDomainNameFromIntegrationType(
  integrationType?: IntegrationType
) {
  switch (integrationType?.toLowerCase()) {
    case IntegrationType.Datadog:
      return 'US1'
    case IntegrationType.DatadogUS3:
      return 'US3'
    case IntegrationType.DatadogUS5:
      return 'US5'
    case IntegrationType.DatadogEU:
      return 'EU'
    case IntegrationType.DatadogUS1Fed:
      return 'US1 FED'
    default:
      return ''
  }
}

export function getDomainNameFromIntegration(
  integration: TeamExternalIntegration
) {
  const { type: integrationType } = integration

  switch (integrationType?.toLowerCase()) {
    case IntegrationType.Datadog:
      return 'US1'
    case IntegrationType.DatadogUS3:
      return 'US3'
    case IntegrationType.DatadogUS5:
      return 'US5'
    case IntegrationType.DatadogEU:
      return 'EU'
    case IntegrationType.DatadogUS1Fed:
      return 'US1 FED'
    case IntegrationType.AWS:
      return integration.domain
    default:
      return ''
  }
}

export function formatExistingStatusCheck(
  data: StatusCheck
): ServiceStatusCheckFromStatusCheck {
  const integrationType = data.teamExternalIntegration?.observabilityToolType
    ? getIntegrationTypeFromObservabilityToolTypeAndDomain(
        data.teamExternalIntegration?.observabilityToolType,
        data.teamExternalIntegration?.domain
      )
    : data.teamExternalIntegration?.type?.toLowerCase()

  return {
    category: data.category,
    id: data.identifier === 'duplicate' ? '' : data.identifier,
    isContinuous: data.isContinuous,
    integration: {
      type: integrationType ?? IntegrationType.Custom,
      observabilityToolType:
        data.teamExternalIntegration?.observabilityToolType,
      domain:
        data.teamExternalIntegration?.domain ||
        getDomainNameFromIntegrationType(data.teamExternalIntegration?.type),
      name: data.teamExternalIntegration?.name || 'Custom',
      isPrivateNetworkIntegration: data.isPrivateNetwork,
      isConfigured: Boolean(data.teamExternalIntegration?.name),
      url:
        // Raw URL is the source of truth for the user form values
        data.rawEndpointConfiguration?.url ?? data.endpointConfiguration?.url,
      headers: Object.entries(data.endpointConfiguration?.headers ?? {}).map(
        ([key, value]) => ({
          key,
          value,
          label: 'Value'
        })
      )
    } as TeamExternalIntegration,
    name: data.name,
    description: data.description,
    ...(data.teamExternalIntegration?.observabilityToolType ===
      ObservabilityToolType.AWS &&
      getAWSIntegrationSpecificValues(data.teamExternalIntegration)),
    ...(data.endpointConfiguration && {
      endpointConfiguration: data.endpointConfiguration
    }),
    evaluationConfiguration: {
      isCustomRequestTimeout: Boolean(
        data.evaluationConfiguration?.okLatencyMaxMs
      ),
      healthyStatusCode: data.evaluationConfiguration?.okStatusCodes?.[0] ?? '',
      requestTimeout: data.evaluationConfiguration?.okLatencyMaxMs,
      ...(data.evaluationConfiguration.responseBodyEvaluation
        ? {
            responseBodyEvaluation: {
              op: data.evaluationConfiguration.responseBodyEvaluation.op,
              predicates:
                data.evaluationConfiguration.responseBodyEvaluation
                  .predicates || []
            }
          }
        : { responseBodyEvaluation: { op: 'AND', predicates: [] } })
    },
    pollingIntervalSeconds: data.pollingIntervalSeconds
      ? `${data.pollingIntervalSeconds}`
      : `${DEFAULT_POLL_INTERVAL}`
  }
}

export function getIntegrationTypeFromObservabilityToolTypeAndDomain(
  observabilityToolType,
  domain
) {
  if (observabilityToolType === ObservabilityToolType.DATADOG) {
    switch (domain) {
      case 'US3':
        return IntegrationType.DatadogUS3
      case 'US5':
        return IntegrationType.DatadogUS5
      case 'EU':
        return IntegrationType.DatadogEU
      case 'US1 FED':
        return IntegrationType.DatadogUS1Fed
      default:
        return IntegrationType.Datadog
    }
  }

  return observabilityToolType?.toLowerCase()
}

function getExternalIntegrationUrl(integration) {
  const { observabilityToolType, domain, type, url } = integration

  const integrationType = type?.toLowerCase()

  // Check for specific observability tool types OR a custom load generator
  if (
    observabilityToolType === ObservabilityToolType.CUSTOM ||
    observabilityToolType === ObservabilityToolType.AWS ||
    observabilityToolType === ObservabilityToolType.GRAFANA ||
    observabilityToolType === ObservabilityToolType.DYNATRACE ||
    observabilityToolType === ObservabilityToolType.APPDYNAMICS ||
    integrationType === IntegrationType.LoadGeneratorCustom
  ) {
    return url
  }

  // Check for remaining observability tool types
  if (observabilityToolType) {
    return ENDPOINT_URLS[
      getIntegrationTypeFromObservabilityToolTypeAndDomain(
        observabilityToolType,
        domain
      )
    ]
  }

  // Indicates a non-custom Load generator
  return ENDPOINT_URLS[integrationType]
}

export const convertResponseToTeamExternalIntegration = (
  data: any
): TeamExternalIntegration[] => {
  return (
    data?.integrations?.map((integration) => {
      const integrationType = integration.observabilityToolType
        ? getIntegrationTypeFromObservabilityToolTypeAndDomain(
            integration.observabilityToolType,
            integration.domain
          )
        : integration.type?.toLowerCase()

      const isAWS = integrationType === IntegrationType.AWS

      // This is a special case for Datadog US1 domain. The API response for this integration does not contain the domain,
      // so in order to facilitate Observability Tool authentication and Health Check creation, we manually add in the
      // "US1" domain.
      if (integrationType === IntegrationType.Datadog) {
        integration.domain = 'US1'
      }

      return {
        id: integration?.id,
        name: integration?.name,
        domain: integration?.domain,
        observabilityToolType: integration?.observabilityToolType,
        url: getExternalIntegrationUrl(integration),
        headers: Object.keys(integration?.headers)
          .sort((a, b) => a.localeCompare(b))
          .map((k) => ({
            key: k,
            value: integration.headers[k],
            label: teamExternalIntegrationsObject[
              integrationType
            ]?.headers.find((h) => h.key === k)?.label
          })),
        isConfigured: true,
        type: integrationType,
        isPrivateNetworkIntegration: integration.privateNetwork,
        lastAuthenticationStatus: integration.lastAuthenticationStatus,
        canBeUsedAsStatusCheck: integration.canBeUsedAsStatusCheck,
        sortIndex:
          teamExternalIntegrationsObject[integrationType]?.sortIndex ?? 6,
        ...(isAWS && {
          integrationSpecificValues:
            getAWSIntegrationSpecificValues(integration)
        })
      }
    }) ?? []
  ).sort((a, b) => a.sortIndex - b.sortIndex)
}

export const removeSavedTeamExternalIntegrations = (
  integrations: TeamExternalIntegration[],
  savedIntegrations: TeamExternalIntegration[]
): TeamExternalIntegration[] => {
  const savedIntegrationsNames = savedIntegrations.map((i) => i.name)
  return integrations.filter((i) => !savedIntegrationsNames.includes(i.name))
}

export const completeTeamExternalIntegrations = (
  externalIntegrationType: ExternalIntegrationType,
  integrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  switch (externalIntegrationType) {
    case ExternalIntegrationType.StatusCheck:
      return completeStatusCheckIntegrations(integrations, includeCustom)
    case ExternalIntegrationType.LoadGenerator:
      return completeLoadGeneratorIntegrations(integrations, includeCustom)
  }
}

export const getUnsavedExternalIntegrations = (
  externalIntegrationType: ExternalIntegrationType,
  savedTeamExternalIntegrations: TeamExternalIntegration[]
) => {
  return removeSavedTeamExternalIntegrations(
    completeTeamExternalIntegrations(externalIntegrationType, [], true),
    savedTeamExternalIntegrations
  )
}

export const getAllExternalIntegrations = (
  externalIntegrationType: ExternalIntegrationType,
  savedTeamExternalIntegrations: TeamExternalIntegration[]
) => {
  return completeTeamExternalIntegrations(
    externalIntegrationType,
    savedTeamExternalIntegrations,
    true
  )
}

export const completeStatusCheckIntegrations = (
  integrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  const completedIntegrations = [...integrations]
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Datadog
    )
  ) {
    completedIntegrations.push(dataDogStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.DatadogUS3
    )
  ) {
    completedIntegrations.push(dataDogUS3StatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.DatadogUS5
    )
  ) {
    completedIntegrations.push(dataDogUS5StatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.DatadogEU
    )
  ) {
    completedIntegrations.push(dataDogEUStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.DatadogUS1Fed
    )
  ) {
    completedIntegrations.push(dataDogUS1FedStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Pagerduty
    )
  ) {
    completedIntegrations.push(pagerDutyStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Newrelic
    )
  ) {
    completedIntegrations.push(newRelicStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Prometheus
    )
  ) {
    completedIntegrations.push(prometheusStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Grafana
    )
  ) {
    completedIntegrations.push(grafanaStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Dynatrace
    )
  ) {
    completedIntegrations.push(dynatraceStatusCheckIntegration)
  }
  if (
    !integrations.find(
      (i) => i?.type?.toLowerCase() === IntegrationType.Appdynamics
    )
  ) {
    completedIntegrations.push(appdynamicsStatusCheckIntegration)
  }

  if (includeCustom) {
    completedIntegrations.push(customStatusCheckIntegration)
  }

  // always push a local instance of the awsStatusCheckIntegration object since all AWS integrations will have the same `aws` type
  completedIntegrations.push(awsStatusCheckIntegration)

  return completedIntegrations.sort((a, b) => a.sortIndex - b.sortIndex)
}

export const completeLoadGeneratorIntegrations = (
  integrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  const completedIntegrations = [...integrations]
  if (
    !integrations.find((i) => i?.type?.toLowerCase() === IntegrationType.K6)
  ) {
    completedIntegrations.push(k6LoadGeneratorIntegration)
  }
  if (includeCustom) {
    completedIntegrations.push(customLoadGeneratorIntegration)
  }
  return completedIntegrations.sort((a, b) => a.sortIndex - b.sortIndex)
}

export function computeResponseBodyEvaluation(
  integration: TeamExternalIntegration
): ResponseBodyEvaluation {
  switch (integration?.type ?? IntegrationType.Custom) {
    case IntegrationType.Datadog:
      return datadogResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogUS3:
      return datadogUS3ResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogUS5:
      return datadogUS5ResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogEU:
      return datadogEUResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogUS1Fed:
      return datadogUS1FedResponseBodyEvaluationConfiguration
    case IntegrationType.Pagerduty:
      return pagerDutyResponseBodyEvaluationConfiguration
    case IntegrationType.Newrelic:
      return computeNewRelicResponseBodyEvaluationConfiguration(integration.url)
    case IntegrationType.Grafana:
      return grafanaResponseBodyEvaluationConfiguration
    case IntegrationType.Dynatrace:
      return dynatraceResponseBodyEvaluationConfiguration
    case IntegrationType.Appdynamics:
      return appdynamicsResponseBodyEvaluationConfiguration
    case IntegrationType.AWS:
      return awsResponseBodyEvaluationConfiguration
  }
}

export async function convertToApiUrl(
  integration: TeamExternalIntegration,
  teamId: string
): Promise<string> {
  if (!integration?.url) {
    return ''
  }

  switch (integration.type) {
    case IntegrationType.Datadog:
      return convertToDatadogApiUrl(integration.url)
    case IntegrationType.DatadogUS3:
      return convertToDatadogUS3ApiUrl(integration.url)
    case IntegrationType.DatadogUS5:
      return convertToDatadogUS5ApiUrl(integration.url)
    case IntegrationType.DatadogEU:
      return convertToDatadogEUApiUrl(integration.url)
    case IntegrationType.DatadogUS1Fed:
      return convertToDatadogUS1FedApiUrl(integration.url)
    case IntegrationType.Pagerduty:
      return await convertToPagerDutyUrl(integration, teamId)
    case IntegrationType.Newrelic:
      return convertToNewRelicUrl(integration.url)?.trim()
    case IntegrationType.Grafana:
      return convertToGrafanaUrl(integration.url)
    case IntegrationType.Dynatrace:
      return convertToDynatraceUrl(integration.url)
    case IntegrationType.Appdynamics:
      return convertToAppDynamicsUrl(integration.url)
    case IntegrationType.K6:
      return convertToK6Url(integration.url)?.trim()
    case IntegrationType.AWS:
      return convertToAWSApiUrl(integration.url)
    default:
      return integration.url?.trim()
  }
}

export function convertToBaseUrl(integration) {
  if (!integration?.url) {
    return ''
  }

  switch (integration.type) {
    case IntegrationType.Grafana:
      return convertToGrafanaBaseUrl(integration.url)
    case IntegrationType.Dynatrace:
      return convertToDynatraceBaseUrl(integration.url)
    case IntegrationType.Appdynamics:
      return convertToAppDynamicsBaseUrl(integration.url)
    default:
      return integration.url?.trim()
  }
}

const isPredicateEmpty = (predicate) => {
  return !predicate.jpQuery?.trim() && !predicate.rValue?.trim()
}

export const formatResponseBodyEvaluationPredicates = ({
  op,
  predicates
}: ResponseBodyEvaluation) => {
  const hasNoPredicates =
    predicates?.length === 1 && isPredicateEmpty(predicates[0])

  return {
    op,
    predicates: hasNoPredicates
      ? []
      : predicates?.map((p) => ({
          ...p,
          jpQuery: p.jpQuery?.startsWith('.') ? p.jpQuery : `.${p.jpQuery}`
        })) ?? []
  }
}

export const addAuthorizationPrefixIfNeeded = (
  type: IntegrationType,
  authHeader: StatusCheckHeader
) => {
  const OBFUSCATION_PREFIX = '****'
  if (
    authHeader.key === 'Authorization' &&
    !authHeader.value.trim().startsWith(OBFUSCATION_PREFIX)
  ) {
    switch (type) {
      case IntegrationType.Pagerduty:
        return authHeader.value.trim().startsWith('Token token=')
          ? authHeader.value
          : `Token token=${authHeader.value.trim()}`
      case IntegrationType.Grafana:
      case IntegrationType.Appdynamics:
        return authHeader.value.trim().startsWith('Bearer ')
          ? authHeader.value
          : `Bearer ${authHeader.value.trim()}`
      case IntegrationType.Dynatrace:
        return authHeader.value.trim().startsWith('Api-Token ')
          ? authHeader.value
          : `Api-Token ${authHeader.value.trim()}`
      case IntegrationType.K6:
        return authHeader.value.trim().startsWith('token ')
          ? authHeader.value
          : `token ${authHeader.value.trim()}`
      default:
        return authHeader.value
    }
  }
  return authHeader.value
}

export const prepareHeaders = (integration: TeamExternalIntegration) => {
  const headers = integration.headers.reduce((result, item) => {
    result[item.key] = addAuthorizationPrefixIfNeeded(integration.type, item)
    return result
  }, {})

  if (integration.type === IntegrationType.Dynatrace) {
    if (!('Content-Type' in headers)) {
      headers['Content-Type'] = 'application/json'
    }
  }
  return headers
}

async function getResponseBodyEvaluationByIntegrationType(
  integration: TeamExternalIntegration,
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration,
  teamId
) {
  if (
    evaluationConfiguration.responseBodyEvaluation &&
    (integration.type === IntegrationType.Custom ||
      integration.observabilityToolType === ObservabilityToolType.CUSTOM)
  ) {
    return formatResponseBodyEvaluationPredicates(
      evaluationConfiguration.responseBodyEvaluation
    )
  } else if (
    integration.type === IntegrationType.Grafana ||
    integration.observabilityToolType === ObservabilityToolType.GRAFANA
  ) {
    return formatResponseBodyEvaluationPredicatesGrafana(
      await convertToApiUrl(integration, teamId)
    )
  } else {
    computeResponseBodyEvaluation(integration)
  }
}

export async function testStatusCheckEvaluation({
  endpointConfiguration,
  evaluationConfiguration,
  endpointResponse,
  integration,
  teamId,
  onError,
  onSuccess
}: {
  endpointConfiguration: EndpointConfiguration | null
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration
  endpointResponse: EndpointResponse
  integration: TeamExternalIntegration
  teamId: string
  onError: (err: unknown) => void
  onSuccess: (evaluationResults: EvaluationResults) => void
}) {
  const teamIntegrationType =
    getIntegrationTypeFromObservabilityToolTypeAndDomain(
      integration.observabilityToolType,
      integration.domain
    )

  const integrationUrl =
    (await convertToApiUrl(integration, teamId)) ||
    teamExternalIntegrationsObject?.[teamIntegrationType]?.authUrl

  if (parseUrl(integrationUrl) === false) {
    onError(new Error(`integration url (${integrationUrl}) is not a valid url`))
    return
  }

  const responseBodyEvaluation =
    await getResponseBodyEvaluationByIntegrationType(
      integration,
      evaluationConfiguration,
      teamId
    )

  const request = {
    evaluationConfiguration: {
      ...(evaluationConfiguration.requestTimeout !== null && {
        okLatencyMaxMs: evaluationConfiguration.requestTimeout
      }),
      okStatusCodes: [evaluationConfiguration.healthyStatusCode],
      responseBodyEvaluation
    } as EvaluationConfiguration
  }

  try {
    if (integration.isPrivateNetworkIntegration) {
      const { data } = await agent.post(
        `integration-invocations?teamId=${teamId}`,
        buildTeamExternalEndpointConfigurationRequest(
          integration,
          integrationUrl,
          undefined,
          undefined,
          endpointConfiguration ?? undefined
        )
      )

      const invocationId = data?.invocationId
      await asyncRetryWithBackoff(500, 20 * 1000, async () => {
        try {
          const { data, status } = await agent.post(
            `integration-invocations/${invocationId}/run-test-evaluation?teamId=${teamId}`,
            request
          )

          const { errorMessage, testEndpointResponse, testEvaluationResults } =
            data

          if (status === 204) {
            //status is No Content - not ready yet
            return false
          } else if (errorMessage) {
            onError(new Error(errorMessage))

            return true
          }

          isValidStatusCodeOrRange(
            testEndpointResponse?.httpStatusCode?.toString()
          )
            ? onSuccess(testEvaluationResults)
            : onError(new Error(errorMessage))

          return true
        } catch (err) {
          onError(err)

          return true
        }
      })
    } else {
      const { data } = await agent.post(
        `status-checks/run-test-evaluation?teamId=${teamId}`,
        {
          ...request,
          testEndpointResponse: {
            ...endpointResponse,
            contentType: endpointResponse.contentType || 'text/plain'
          }
        }
      )

      data?.testEvaluationResults
        ? onSuccess(data.testEvaluationResults)
        : onError(new Error('No evaluation results received'))
    }
  } catch (err) {
    onError(err)
  }
}

export function buildTeamExternalEndpointConfigurationRequest(
  integration: TeamExternalIntegration,
  integrationUrl: string,
  statusCheckId?: string,
  authenticate?: boolean,
  endpointConfiguration?: EndpointConfiguration
) {
  const isCustomIntegration = integration.type === IntegrationType.Custom
  const isPostMethod = endpointConfiguration?.method === 'POST'
  const endpointConfigurationHeadersFromIntegration =
    authenticate ||
    isCustomIntegration ||
    (!statusCheckId && !integration.isConfigured)
      ? prepareHeaders(integration)
      : undefined

  return {
    endpointConfiguration: {
      // Including additional endpoint configuration details is only valid
      // when we have configured the method to be 'POST'
      ...(isPostMethod && endpointConfiguration),
      // We have to consider mixing in headers from the Authentication details,
      // onto those that were defined via the create/edit Status Check form.
      headers: {
        ...endpointConfigurationHeadersFromIntegration,
        // This is only valid if we have configured the method to be 'POST'
        ...(isPostMethod &&
          endpointConfiguration?.headers?.['content-type'] && {
            ['content-type']: endpointConfiguration.headers['content-type']
          })
      },
      url: encodeQueryParams(integrationUrl)
    },
    // only need to send `integrationSpecificValues` when we are
    // in the authenticate flow. i.e. we don't need to send this
    // in the create/edit health check flow
    ...(integration.type === IntegrationType.AWS &&
      authenticate && {
        integrationSpecificValues: getAWSIntegrationSpecificValues(integration)
      }),
    ...(integration.isConfigured && {
      teamExternalIntegration: {
        observabilityToolType: integration.observabilityToolType,
        domain: integration.domain,
        name: integration.name
      }
    }),
    ...(!integration.isConfigured &&
      statusCheckId && {
        webhookId: statusCheckId
      })
  }
}

export const isEncoded = (uri) => uri !== decodeURIComponent(uri || '')

export const encodeQueryParams = (url) => {
  if (!isEncoded(url)) {
    let Url
    try {
      Url = new URL(url)
    } catch (e) {
      // If we get an invalid URL just give up
      return url
    }

    const fragment = Url.hash
    const [requestUrl, queryParamsString] = url
      .replace(fragment, '')
      .split(/\?(.*)/s)

    if (queryParamsString) {
      const encodedParams = []
      queryParamsString.split('&').forEach((queryParam) => {
        const [param, value] = queryParam.split('=')
        encodedParams.push(
          value ? `${param}=${encodeURIComponent(value)}` : param
        )
      })
      return fragment
        ? `${requestUrl}?${encodedParams.join('&')}${fragment}`
        : `${requestUrl}?${encodedParams.join('&')}`
    }
  }

  return url
}

export const isValidStatusCodeOrRange = (v) => /^2\d{2}(-2\d{2})?$/.test(v) // 2xx or 2xx-2xx
